import React, { useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useRecoilState } from 'recoil'
import { useForm, useController } from 'react-hook-form'
import {
  isEqual,
  addDays,
  format,
  parse,
  subDays,
  differenceInDays,
  parseISO,
  formatISO,
  isWithinInterval,
} from 'date-fns'
import { toInteger, isInteger } from 'lodash'
import { DateLimit } from 'flatpickr/dist/types/options'

import { GetVenueDetailQuery } from 'generated/graphcms'

import Spinner from '@/components/ui/Spinner/Spinner'
import Alert from '@/components/ui/Alert/Alert'
import FormInputNumber from 'components/ui/FormInputNumber/FormInputNumber'
import Button from '@/components/ui/Button/Button'
import { REQUEST_SECOND_STEP } from '@/components/shared/VenueForm/VenueFormSecondStep'
import { REQUEST_SUCCESS_STEP } from '@/components/shared/VenueForm/VenueFormSuccess'
import { type RangeMap, type RangeArray } from '@/components/ui/DateRangePicker/DateRangePicker'

import { CachedRequest } from '../../../hooks/useSubmitVenueRequest'
import { firstStep } from '../../../store/venueRequest'
import { APP_ROUTES, MIN_GUESTS } from '../../../utils/constants'
import isEventExpired from '../../../utils/isEventExpired'
import getVenueUnavailableDates from '../../../api/getVenueUnavailableDates'

export interface Range {
  from: string
  to: string
}

export interface Props {
  venue: GetVenueDetailQuery['venue']
}

const REQUEST_FIRST_STEP = 'requestFirstStep'

const MIN_ROOMS_SINGLE = 0
const MIN_ROOMS_DOUBLE = 0

const VenueFormFirstStep: React.FC<Props> = ({ venue }) => {
  const MIN_VENUE_GROUP_SIZE = venue?.minGroupSize || MIN_GUESTS
  const MAX_VENUE_GROUP_SIZE = venue?.groupSize || Infinity
  const MAX_VENUE_ROOMS_AMOUNT = venue?.roomsTotal || Infinity

  const { t } = useTranslation()
  const TRANSLATION = 'venue.requestForm.firstStep'
  const router = useRouter()
  const {
    locale,
    query: { groupSize: groupSizeQuery },
  } = router

  const DateRangePicker = dynamic(() => import('components/ui/DateRangePicker/DateRangePicker'), {
    ssr: false,
  })

  const [isFormReady, setIsFormReady] = useState(false)
  const [formValues, setFormValues] = useRecoilState(firstStep)

  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [isReset, setIsReset] = useState(false)

  const [roomError, setRoomError] = useState<string | null>(null)

  const [maxGuestsWarning, setMaxGuestsWarning] = useState('')
  const [maxRoomsWarning, setMaxRoomsWarning] = useState('')

  const [isDisabledDateLimitsFetched, setIsDisabledDateLimitsFetched] = useState(false)
  const [venueDisabledDateLimits, setVenueDisabledDateLimits] = useState<DateLimit[]>([])
  const [venueDisabledDatesMap, setVenueDisabledDatesMap] = useState<RangeMap>()
  const [venueDisabledDatesSorted, setVenueDisabledDatesSorted] = useState<RangeArray>()

  const {
    setValue,
    clearErrors,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      ...formValues,
      groupSize: `${MIN_VENUE_GROUP_SIZE}`,
      roomsSingle: `${MIN_VENUE_GROUP_SIZE}`,
    },
  })

  const { field: arrivalDateField } = useController({
    name: 'dateArrival',
    control,
    rules: { required: true },
  })

  const { field: departureDateField } = useController({
    name: 'dateDeparture',
    control,
    rules: { required: true },
  })

  const { field: groupSizeField } = useController({
    name: 'groupSize',
    control,
    rules: {
      required: true,
      min: MIN_VENUE_GROUP_SIZE,
      max: {
        value: MAX_VENUE_GROUP_SIZE,
        message: t(`${TRANSLATION}.maxGuestsInVenue`, { count: venue?.groupSize }),
      },
    },
  })

  const { field: roomsSingleField } = useController({
    name: 'roomsSingle',
    control,
    rules: { required: true, min: MIN_ROOMS_SINGLE },
  })

  const { field: roomsDoubleField } = useController({
    name: 'roomsDouble',
    control,
    rules: { required: true, min: MIN_ROOMS_DOUBLE },
  })

  const dateArrival = arrivalDateField.value
  const dateDeparture = departureDateField.value
  const groupSize = groupSizeField.value
  const roomsSingle = roomsSingleField.value
  const roomsDouble = roomsDoubleField.value

  const dateChange = (value: [(string | Date)?, (string | Date)?]) => {
    arrivalDateField.onChange(value[0])
    departureDateField.onChange(value[1] || '')
  }

  function isDateInDisabledList(targetDate: Date | string) {
    const formattedTargetDate = new Date(targetDate)

    return venueDisabledDateLimits.some((date) => {
      if (typeof date === 'string') {
        return isEqual(parseISO(date), formattedTargetDate)
      }
      if (typeof date === 'object' && 'from' in date && 'to' in date) {
        const fromDate = parseISO(date.from as string)
        const toDate = parseISO(date.to as string)

        return isWithinInterval(
          formattedTargetDate,
          fromDate <= toDate ? { start: fromDate, end: toDate } : { start: toDate, end: fromDate }
        )
      }

      return false
    })
  }

  const getCachedForm = () => {
    const cachedStepString = localStorage?.getItem(REQUEST_FIRST_STEP)

    if (cachedStepString) {
      const cachedStep = JSON.parse(cachedStepString)

      if (
        cachedStep?.dateArrival &&
        cachedStep?.dateDeparture &&
        !isEventExpired(cachedStep.dateArrival) &&
        !isDateInDisabledList(cachedStep.dateArrival) &&
        !isDateInDisabledList(cachedStep.dateDeparture)
      ) {
        dateChange([cachedStep.dateArrival, cachedStep.dateDeparture])
      } else {
        dateChange(['', ''])
      }

      setValue('groupSize', groupSizeQuery || cachedStep.groupSize || MIN_VENUE_GROUP_SIZE)
      if (cachedStep?.roomsSingle) setValue('roomsSingle', cachedStep.roomsSingle)
      if (cachedStep?.roomsDouble) setValue('roomsDouble', cachedStep.roomsDouble)
    }

    setIsFormReady(true)
  }

  useEffect(() => {
    if (isDisabledDateLimitsFetched) {
      getCachedForm()
    }
  }, [isDisabledDateLimitsFetched])

  const createDateMap = (dates: Range[]): RangeMap => {
    const dateMap = new Map()

    dates.forEach(({ from, to }) => {
      if (!dateMap.has(from)) {
        dateMap.set(from, { from: true, to: false })
      } else {
        dateMap.set(from, { ...dateMap.get(from), from: true })
      }

      if (!dateMap.has(to)) {
        dateMap.set(to, { from: false, to: true })
      } else {
        dateMap.set(to, { ...dateMap.get(to), to: true })
      }
    })

    return dateMap
  }

  const processDates = (dates: Range[]): Range[] =>
    dates
      .filter(({ from, to }) => {
        const diffDays = differenceInDays(parseISO(to), parseISO(from))

        return diffDays !== 1
      })
      .map(({ from, to }) => {
        const newFrom = addDays(parseISO(from), 1)
        const newTo = subDays(parseISO(to), 1)

        const formattedFrom = formatISO(newFrom, { representation: 'date' })
        const formattedTo = formatISO(newTo, { representation: 'date' })

        return { from: formattedFrom, to: formattedTo }
      })

  const fetchVenueUnavailableDates = async () => {
    if (venue) {
      try {
        const res = await getVenueUnavailableDates(venue.id)

        if (res.data?.status === 'success') {
          const unavailabilityDatesResponse: Range[] = res.data?.data?.unavailability || []

          const dateMap = createDateMap(unavailabilityDatesResponse)
          setVenueDisabledDatesMap(dateMap)

          const dateArray = Array.from(dateMap).sort(
            ([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()
          )
          setVenueDisabledDatesSorted(dateArray)

          // check in/out dates are partly available (but not for one-day book)
          const totalUnavailableDates = processDates(unavailabilityDatesResponse)

          // disabling dates with check in and checkout in one/close day from different groups
          const overlappedDates = dateArray
            .filter(([date, { from, to }]) => {
              if (from && to) return true

              if (from) {
                const parsedDate = parse(date, 'yyyy-MM-dd', new Date())
                const yesterday = subDays(parsedDate, 1)
                const formattedYesterday = format(yesterday, 'yyyy-MM-dd')

                return dateMap?.get(formattedYesterday)?.from
              }

              return false
            })
            .map(([date]) => date)

          const disabledDates = [...totalUnavailableDates, ...overlappedDates]

          setVenueDisabledDateLimits(disabledDates)
        }
      } catch (e) {
        // empty
      }
      setIsDisabledDateLimitsFetched(true)
    }
  }

  useEffect(() => {
    fetchVenueUnavailableDates()
  }, [venue])

  const groupSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newGroupSize = `${toInteger(parseInt(e.target.value, 10))}`

    if (isInteger(+newGroupSize)) {
      setValue('groupSize', newGroupSize)

      const shortage = +newGroupSize - +roomsDouble * 2

      if (shortage > 0) {
        setValue('roomsSingle', `${shortage}`)
        clearErrors('roomsSingle')
      } else {
        setValue('roomsSingle', '0')
      }
    }
  }

  const roomsSingleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRoomsSingle = `${toInteger(parseInt(e.target.value, 10))}`

    if (isInteger(+newRoomsSingle)) {
      setValue('roomsSingle', newRoomsSingle)

      const shortage = +groupSize - +newRoomsSingle

      if (shortage > 0) {
        setValue('roomsDouble', `${Math.ceil(shortage / 2)}`)
      } else {
        setValue('roomsDouble', '0')
      }
    }
  }

  const roomsDoubleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRoomsDouble = `${toInteger(parseInt(e.target.value, 10))}`

    if (isInteger(+newRoomsDouble)) {
      setValue('roomsDouble', newRoomsDouble)

      const shortage = +groupSize - +newRoomsDouble * 2

      if (shortage > 0) {
        setValue('roomsSingle', `${shortage}`)
      } else {
        setValue('roomsSingle', '0')
      }
    }
  }

  const checkRoomsValidity = () => {
    const totalRooms = +roomsSingle + +roomsDouble * 2

    if (totalRooms > +groupSize + (roomsDouble ? 1 : 0)) {
      return setRoomError(t(`${TRANSLATION}.insufficientRooms`))
    }
    if (totalRooms < +groupSize) {
      return setRoomError(t(`${TRANSLATION}.excessivelyRooms`))
    }

    setRoomError(null)
    return true
  }

  useEffect(() => {
    if (isSubmitted) checkRoomsValidity()
  }, [groupSize, roomsSingle, roomsDouble, isSubmitted])

  useEffect(() => {
    // store previous requests dates/group in LS and prevent making same requests
    if (venue?.slug) {
      const prevRequestsJSON = localStorage.getItem(venue?.slug) || ''

      if (prevRequestsJSON) {
        const isSomeAlreadySubmitted = JSON.parse(prevRequestsJSON)?.some(
          (request: CachedRequest) =>
            +groupSize === +request.groupSize &&
            isEqual(new Date(request.dateArrival), new Date(dateArrival)) &&
            isEqual(new Date(request.dateDeparture), new Date(dateDeparture))
        )

        setAlreadySubmitted(isSomeAlreadySubmitted)
      }
    }
  }, [venue, dateArrival, dateDeparture, groupSize])

  useEffect(() => {
    if (venue?.groupSize && +groupSize > +venue.groupSize) {
      setMaxGuestsWarning(t(`${TRANSLATION}.maxGuestsInVenue`, { count: venue?.groupSize }))
    } else if (maxGuestsWarning) {
      setMaxGuestsWarning('')
    }
  }, [groupSize])

  useEffect(() => {
    if (venue?.roomsTotal && +roomsSingle + +roomsDouble > +venue.roomsTotal) {
      setMaxRoomsWarning(t(`${TRANSLATION}.maxRoomsInVenue`, { count: venue?.roomsTotal }))
    } else if (maxRoomsWarning) {
      setMaxRoomsWarning('')
    }
  }, [roomsSingle, roomsDouble])

  // TODO #Conservation prevent users from starting new requests
  const disableNewRequests = true;

  return (
    <div className="space-y-2 md:flex md:flex-col md:space-y-5">
      <p className="font-semibold md:text-xl">{t('venue.requestForm.requestQuoteButton')}</p>
      {isFormReady ? (
            <form
              className="flex flex-col space-y-4 rounded-lg border border-gray-200 p-4 text-sm md:px-6 md:py-7 md:text-lg"
              onSubmit={handleSubmit((data) => {
                setFormValues(data)

                if (checkRoomsValidity()) {
                  if (isReset) {
                    localStorage.setItem(REQUEST_SECOND_STEP, '')
                    localStorage.setItem(REQUEST_SUCCESS_STEP, '')
                  }

                  localStorage.setItem(REQUEST_FIRST_STEP, JSON.stringify(data))
                  router.push({ pathname: `${APP_ROUTES.VENUE_FORM}/${venue?.slug}`, query: 'page=2' })
                }
              })}
            >
              {disableNewRequests ? (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="shrink-0">
                      ⚠️
                    </div>
                    <div className="ml-3">
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          {t('venue.requestForm.newRequestsDisabledWarning')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
              <div className="flex justify-center space-x-4">
                <DateRangePicker
                  value={[dateArrival, dateDeparture]}
                  onChange={dateChange}
                  title={t(`${TRANSLATION}.arrivalDateInput`)}
                  locale={locale}
                  secondTitle={t(`${TRANSLATION}.departureDateInput`)}
                  firstInputError={!!errors.dateArrival}
                  secondInputError={!!errors.dateDeparture}
                  options={{ disable: venueDisabledDateLimits }}
                  disabledDatesMap={venueDisabledDatesMap}
                  disabledDatesSortedArray={venueDisabledDatesSorted}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-3">
                <FormInputNumber
                  {...groupSizeField}
                  onChange={groupSizeChange}
                  title={t(`${TRANSLATION}.guestsInput`)}
                  min={MIN_VENUE_GROUP_SIZE}
                  max={MAX_VENUE_GROUP_SIZE}
                  tooltipMin={
                    venue?.minGroupSize
                      ? t(`${TRANSLATION}.minGuestsInVenue`, { count: venue?.minGroupSize })
                      : t('filters.guestsMinHint', { count: MIN_GUESTS })
                  }
                  tooltipMax={t(`${TRANSLATION}.maxGuestsInVenue`, { count: venue?.groupSize })}
                  className="md:col-span-3 lg:col-span-4 xl:col-span-1"
                  error={!!errors.groupSize}
                />
                <FormInputNumber
                  {...roomsSingleField}
                  onChange={roomsSingleChange}
                  title={t(`${TRANSLATION}.singleRoomsInput`)}
                  min={MIN_ROOMS_SINGLE}
                  max={MAX_VENUE_ROOMS_AMOUNT}
                  tooltipMax={t(`${TRANSLATION}.maxRoomsInVenue`, { count: venue?.roomsTotal })}
                  className="md:col-span-3 lg:col-span-2 xl:col-span-1"
                  error={!!errors.roomsSingle}
                />
                <FormInputNumber
                  {...roomsDoubleField}
                  onChange={roomsDoubleChange}
                  title={t(`${TRANSLATION}.doubleRoomsInput`)}
                  min={MIN_ROOMS_DOUBLE}
                  max={MAX_VENUE_ROOMS_AMOUNT}
                  tooltipMax={t(`${TRANSLATION}.maxRoomsInVenue`, { count: venue?.roomsTotal })}
                  className="md:col-span-3 lg:col-span-2 xl:col-span-1"
                  error={!!errors.roomsDouble}
                />
              </div>

              {(maxGuestsWarning + maxRoomsWarning).length > 0 && (
                <Alert type="warning">
                  <ul className={maxGuestsWarning && maxRoomsWarning ? 'list-inside list-disc' : ''}>
                    <li>{maxGuestsWarning}</li>
                    <li>{maxRoomsWarning}</li>
                  </ul>
                </Alert>
              )}

              {roomError && <Alert>{roomError}</Alert>}
              {!alreadySubmitted ? (
                <Button
                  disabled={alreadySubmitted}
                  variant="secondary"
                  size="lg"
                  className="mx-[15%]"
                  type="submit"
                >
                  {t('venue.requestForm.requestQuoteButton')}
                </Button>
              ) : (
                <>
                  <Alert>{t('venue.requestForm.sameRequestError')}</Alert>
                  <Button
                    onClick={() => {
                      reset()
                      setIsReset(true)
                    }}
                    variant="secondary"
                    size="lg"
                    className="mx-[15%]"
                    type="button"
                  >
                    {t(`${TRANSLATION}.makeAnotherRequestButton`)}
                  </Button>
                </>
              )}
                </>)}
            </form>
      ) : (
        <Spinner />
      )}
    </div>
  )
}

export default VenueFormFirstStep
